import { GetObjectCommand, type S3Client } from '@aws-sdk/client-s3'
import { getJsonObject, paginateList, withS3Client, type S3Io } from './s3'
import firmwareVersions from './firmware-versions.json'

export const FIRMWARE_BUCKET = 'oroblanco-binaries'

const USER_ID = '16d93e22-e06c-418e-92fd-a0c606758566'
const DEVICE = 'OROBLANCO_ONE_C3'

export type FirmwareTarget = {
  project: string
  gitHash: string
}

// Regenerated by scripts/generate-firmware-versions.sh from the newest
// builds in the firmware bucket.
const LATEST_BUILDS: Record<string, { gitHash: string } | undefined> =
  firmwareVersions.projects

function latestTarget(project: string): FirmwareTarget {
  const build = LATEST_BUILDS[project]
  if (!build) {
    throw new Error(
      `No build for ${project} in firmware-versions.json — run scripts/generate-firmware-versions.sh`,
    )
  }
  return { project, gitHash: build.gitHash }
}

export const MP3_PLAYER_FIRMWARE: FirmwareTarget = latestTarget('micropy-mp3-player')

export const ABC_FIRMWARE: FirmwareTarget = latestTarget('micropy-abc')

export const MIDI_SYNTH_FIRMWARE: FirmwareTarget = latestTarget('micropy-synth')

export const PODCAST_PLAYER_FIRMWARE: FirmwareTarget = latestTarget('micropy-podcasts')

export type FirmwareMetadata = Record<string, unknown>

export type Firmware = {
  binary: Uint8Array
  binaryName: string
  metadata: FirmwareMetadata
  metadataName: string
}

export async function fetchFirmware(
  io: S3Io,
  target: FirmwareTarget,
): Promise<Firmware> {
  const prefix = `${USER_ID}/${target.project}/${target.gitHash}/${DEVICE}`
  return withS3Client(io, async (client) => {
    const [binRes, metaRes] = await Promise.all([
      client.send(
        new GetObjectCommand({
          Bucket: FIRMWARE_BUCKET,
          Key: `${prefix}/micropython.bin`,
        }),
      ),
      client.send(
        new GetObjectCommand({
          Bucket: FIRMWARE_BUCKET,
          Key: `${prefix}/metadata.json`,
        }),
      ),
    ])
    if (!binRes.Body || !metaRes.Body) {
      throw new Error('Firmware object body missing')
    }
    const binary = await binRes.Body.transformToByteArray()
    const metaText = await metaRes.Body.transformToString()
    const metadata = JSON.parse(metaText) as FirmwareMetadata
    return {
      binary,
      binaryName: 'micropython.bin',
      metadata,
      metadataName: 'metadata.json',
    }
  })
}

export type FirmwareBuild = {
  gitHash: string
  metadata: FirmwareMetadata
  // metadata.build_time when it's a parseable timestamp, else null.
  buildTime: string | null
}

const BUILD_METADATA_CONCURRENCY = 8

export function parseableTime(v: unknown): string | null {
  return typeof v === 'string' && !Number.isNaN(Date.parse(v)) ? v : null
}

async function getBuildMetadata(
  client: S3Client,
  project: string,
  gitHash: string,
): Promise<FirmwareBuild | null> {
  // A build without (or with corrupt) metadata for this device isn't
  // installable from here — skip it rather than fail the whole scan.
  const result = await getJsonObject(
    client,
    FIRMWARE_BUCKET,
    `${USER_ID}/${project}/${gitHash}/${DEVICE}/metadata.json`,
  )
  if (result.kind !== 'ok') return null
  const metadata = result.value as FirmwareMetadata
  return { gitHash, metadata, buildTime: parseableTime(metadata.build_time) }
}

// Discovers every published build of a firmware project: the gitHash prefixes
// under {USER_ID}/{project}/, each described by its {DEVICE}/metadata.json.
export async function listFirmwareBuilds(
  io: S3Io,
  project: string,
): Promise<FirmwareBuild[]> {
  return withS3Client(io, async (client) => {
    const prefix = `${USER_ID}/${project}/`
    const gitHashes: string[] = []
    for await (const res of paginateList(client, {
      Bucket: FIRMWARE_BUCKET,
      Prefix: prefix,
      Delimiter: '/',
    })) {
      for (const p of res.CommonPrefixes ?? []) {
        if (!p.Prefix) continue
        const name = p.Prefix.slice(prefix.length).replace(/\/$/, '')
        if (name) gitHashes.push(name)
      }
    }
    const builds: FirmwareBuild[] = []
    for (let i = 0; i < gitHashes.length; i += BUILD_METADATA_CONCURRENCY) {
      const chunk = gitHashes.slice(i, i + BUILD_METADATA_CONCURRENCY)
      const results = await Promise.all(
        chunk.map((gitHash) => getBuildMetadata(client, project, gitHash)),
      )
      builds.push(...results.filter((b): b is FirmwareBuild => b !== null))
    }
    return builds
  })
}

// Pulls the firmware package name and build_time out of a toy version's
// metadata.json (its `firmwareMetadata` object). Both absent when the toy
// embeds no firmware. Centralized so every "is there newer firmware" caller
// reads the same fields the same way.
export function firmwareMetaOf(metadata: Record<string, unknown> | null | undefined): {
  packageName: string | null
  buildTime: unknown
} {
  const fw = metadata?.firmwareMetadata
  if (!fw || typeof fw !== 'object' || Array.isArray(fw)) {
    return { packageName: null, buildTime: undefined }
  }
  const f = fw as Record<string, unknown>
  return {
    packageName: typeof f.package_name === 'string' ? f.package_name : null,
    buildTime: f.build_time,
  }
}

// True iff this build is strictly newer than the toy's current firmware.
// False whenever either side lacks a parseable build_time — with no sound
// baseline, nothing may claim to be newer.
export function isNewerBuild(build: FirmwareBuild, currentBuildTime: unknown): boolean {
  const current = parseableTime(currentBuildTime)
  if (!current || build.buildTime === null) return false
  return Date.parse(build.buildTime) > Date.parse(current)
}

export function sortBuildsNewestFirst(builds: FirmwareBuild[]): FirmwareBuild[] {
  return [...builds].sort((a, b) => {
    if (a.buildTime === null) return b.buildTime === null ? 0 : 1
    if (b.buildTime === null) return -1
    return Date.parse(b.buildTime) - Date.parse(a.buildTime)
  })
}

// Builds strictly newer than the toy's current firmware, newest first. When
// the current build_time is missing or unparseable there is nothing sound to
// compare against, so nothing is reported as newer.
export function filterNewerBuilds(
  builds: FirmwareBuild[],
  currentBuildTime: unknown,
): FirmwareBuild[] {
  return sortBuildsNewestFirst(builds.filter((b) => isNewerBuild(b, currentBuildTime)))
}

